/**
 * useLessonProgress
 *
 * Fetches per-lesson Coverage, 3-state status, per-part time, and the
 * Engagement strip (notes / sources / doubts) for the three-dot menu.
 *
 * Backend endpoint: GET /lessons/{id}/progress
 *
 * Honors VITE_DATA_MODE: returns mock or empty data when the page is
 * being exercised without a live backend.
 */

import { useQuery } from '@tanstack/react-query';
import { progressApi, type LessonProgressResponse } from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { queryKeys } from '@/config/query-client';
import { loadMockProgressData } from '@/data';

const MOCK_DELAY_MS = 300;

export function useLessonProgress(lessonId: string | undefined) {
  const { isMock, isEmpty } = useDataMode();

  return useQuery<LessonProgressResponse>({
    // Mode flags stay IN the key: the queryFn returns mock/empty/api data
    // depending on them, so two modes must not share a cache entry.
    queryKey: [...queryKeys.lessons.progress(lessonId ?? ''), isMock, isEmpty],
    queryFn: async () => {
      const id = lessonId as string;
      if (isEmpty)
        return (await loadMockProgressData()).buildEmptyLessonProgress(id);
      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));
        return {
          ...(await loadMockProgressData()).MOCK_LESSON_PROGRESS,
          lesson_id: id,
        };
      }
      return progressApi.getLessonProgress(id);
    },
    enabled: !!lessonId,
    // 30s mirrors the classified-chat hook - fresh enough to feel live
    // without hammering the activity collection on every menu open.
    staleTime: 30_000,
  });
}
